import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDelete } from '@/shared/components/ui/confirm-delete/confirm-delete';
import { TDeleteAssetsProps } from '@/shared/features/assets/delete-assets/delete-assets.types';
import { useLinkedSecretsDeleteAction } from '@/shared/features/assets/delete-assets/use-linked-secrets-delete-action';
import { useHasPermission } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { useGroupsResourcesCount } from '@/shared/hooks/use-groups-resources-count/use-groups-resources-count';
import { useDeleteDeviceMutation } from '@/shared/redux/api/assets/devices/devices-api';
import { useDeleteDocumentMutation } from '@/shared/redux/api/assets/documents/documents-api';
import { useDeleteGatewayMutation } from '@/shared/redux/api/assets/gateways/gateways-api';
import { useDeleteResourceGroupMutation } from '@/shared/redux/api/assets/groups/groups-api';
import { useDeleteProjectMutation } from '@/shared/redux/api/assets/projects/projects-api';
import { useDeleteTagMutation } from '@/shared/redux/api/assets/tags/tags-api';
import { useDeleteWorkstationMutation } from '@/shared/redux/api/assets/workstations/workstations-api';
import { useDeletePipelineConfigurationMutation } from '@/shared/redux/api/jobs/jobs-api';
import {
  DeleteDeviceMutation,
  DeleteDocumentMutation,
  DeleteGatewayMutation,
  DeletePipelineConfigurationMutation,
  DeleteProjectMutation,
  DeleteResourceGroupMutation,
  DeleteTagMutation,
  DeleteWorkstationMutation,
  ResourceGroupResponseType,
} from '@/shared/types/types.generated';

export const DeleteAssets: React.FC<TDeleteAssetsProps> = ({
  assets,
  onSuccess,
  component = 'Button',
  onClose,
  permission,
}) => {
  const { t } = useTranslation();
  const [deleteResourceGroup] = useDeleteResourceGroupMutation();
  const [deleteDevice] = useDeleteDeviceMutation();
  const [deleteGateway] = useDeleteGatewayMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [deletePipelineConfiguration] =
    useDeletePipelineConfigurationMutation();
  const [deleteWorkstation] = useDeleteWorkstationMutation();
  const [deleteTag] = useDeleteTagMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const hasPermission = useHasPermission(permission);
  const linkedSecretIds = assets.flatMap((asset) => {
    if (
      asset.__typename === 'DeviceResponseType' ||
      asset.__typename === 'ProjectResponseType'
    ) {
      return [asset.secretId];
    }

    return [];
  });
  const optionalDeleteAction = useLinkedSecretsDeleteAction(linkedSecretIds);

  const groupIds = assets
    .filter(({ __typename }) => __typename === 'ResourceGroupResponseType')
    .map((item) => (item as ResourceGroupResponseType).groupId);

  const groupsResourcesCount = useGroupsResourcesCount(groupIds);

  const isLoadingCounts = groupIds.some(
    (id) => groupsResourcesCount[id]?.isLoading,
  );

  const hasGroupWithSubItems = () =>
    isLoadingCounts ||
    groupIds.some(
      (id) => (groupsResourcesCount[id]?.allResourcesCount ?? 0) > 0,
    );

  const getTooltip = () => {
    if (hasGroupWithSubItems()) {
      return t('groups:cannot_delete_group_with_subgroups');
    }
    return '';
  };

  const onDelete = async () => {
    const promises: Promise<
      | DeleteResourceGroupMutation
      | DeleteDeviceMutation
      | DeleteGatewayMutation
      | DeletePipelineConfigurationMutation
      | DeleteDocumentMutation
      | DeleteWorkstationMutation
      | DeleteTagMutation
      | DeleteProjectMutation
    >[] = [];

    assets.forEach((item) => {
      if (item.__typename === 'ResourceGroupResponseType') {
        promises.push(deleteResourceGroup({ groupId: item.groupId }).unwrap());
        return;
      }

      if (item.__typename === 'DeviceResponseType') {
        promises.push(deleteDevice({ deviceId: item.deviceId }).unwrap());
        return;
      }

      if (item.__typename === 'GatewayResponseType') {
        promises.push(deleteGateway({ gatewayId: item.gatewayId }).unwrap());
        return;
      }

      if (item.__typename === 'PipelineConfigurationResponseType') {
        promises.push(
          deletePipelineConfiguration({
            pipelineConfigurationId: item.pipelineConfigurationId,
          }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'DocumentResponseType') {
        promises.push(deleteDocument({ documentId: item.documentId }).unwrap());
        return;
      }

      if (item.__typename === 'WorkstationResponseType') {
        promises.push(
          deleteWorkstation({ workstationId: item.workstationId }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'TagResponseType') {
        promises.push(deleteTag({ name: item.name }).unwrap());
        return;
      }

      if (item.__typename === 'ProjectResponseType') {
        promises.push(deleteProject({ projectId: item.projectId }).unwrap());
        return;
      }
    });

    await Promise.all(promises);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <ConfirmDelete
      component={component}
      disabled={hasGroupWithSubItems()}
      deleteMutation={onDelete}
      itemCount={assets.length}
      optionalDeleteAction={optionalDeleteAction}
      tooltip={getTooltip()}
      onSuccess={onSuccess}
      onClose={onClose}
    />
  );
};
